import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

function formatDuration(s) {
  if (!s) return "0 min";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h) return `${h} h ${m} min`;
  return `${m} min`;
}

const SUBJECT_LABEL = { math: "Matematika", czech: "Český jazyk" };
const STATUS_LABEL = { completed: "Dokončeno", in_progress: "Rozpracováno", abandoned: "Opuštěno" };

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [code, setCode] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setError(true));
    api.get("/parent/invitation-code").then((r) => setCode(r.data.code)).catch(() => {});
  }, []);

  if (error) return <div role="alert" className="p-12">Přehled se nepodařilo načíst. <Link to="/testy" className="underline">Otevřít testy</Link> nebo <Link to="/login" className="underline">přihlásit se znovu</Link>.</div>;
  if (!data) {
    return <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-20 caption">Načítání…</div>;
  }

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-16 animate-fade-in">
      {/* Hero greeting */}
      <p className="caption mb-6">Tvůj přehled</p>
      <h1 className="font-serif text-4xl lg:text-5xl text-ink leading-tight">
        Ahoj, <span className="font-serif-italic">{user.first_name}</span>.
      </h1>
      <p className="text-slate mt-4 max-w-prose text-lg">
        Tady je rychlý souhrn tvé přípravy. Když chceš pokračovat, klikni na test níže.
      </p>

      {/* Stats grid */}
      <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-px bg-stone border border-stone rounded-sm overflow-hidden">
        <Stat label="Dokončené testy" value={data.stats.total_completed} />
        <Stat label="Průměrné skóre" value={`${data.stats.average_score}%`} />
        <Stat label="Rozpracované" value={data.stats.total_in_progress} />
        <Stat label="Čas přípravy" value={formatDuration(data.stats.total_time_seconds)} />
      </div>

      {/* Two-column editorial layout */}
      <div className="mt-16 grid grid-cols-12 gap-8">
        {/* Recent attempts */}
        <section className="col-span-12 lg:col-span-8">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-serif text-2xl text-ink">Poslední pokusy</h2>
            <Link to="/testy" className="text-sm text-navy underline underline-offset-4" data-testid="link-all-tests">
              Všechny testy →
            </Link>
          </div>
          {data.recent_attempts.length === 0 ? (
            <p className="font-serif-italic text-slate text-lg">
              Zatím nemáš dokončený žádný test. Pojďme s tím začít.
            </p>
          ) : (
            <ul className="divide-y divide-stone border-y border-stone">
              {data.recent_attempts.map((a) => (
                <li key={a.id} className="py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="caption mb-1">
                      {SUBJECT_LABEL[a.subject] || a.subject} · {STATUS_LABEL[a.status]}
                    </p>
                    <p className="font-serif text-lg text-ink">{a.test_title}</p>
                  </div>
                  <div className="flex items-center gap-6">
                    {a.status === "completed" && (
                      <p className="font-mono tabular text-xl text-navy">
                        {a.total_score}<span className="text-slate"> / {a.max_possible_score}</span>
                      </p>
                    )}
                    {a.status === "completed" ? (
                      <Link to={`/vysledek/${a.id}`} data-testid={`attempt-result-${a.id}`}>
                        <Button variant="outline" size="sm" className="border-ink text-ink rounded-sm hover:bg-lemon">Detail</Button>
                      </Link>
                    ) : (
                      <Link to={`/test/${a.id}`} data-testid={`attempt-resume-${a.id}`}>
                        <Button size="sm" className="bg-navy hover:bg-navy-dark text-parchment rounded-sm">Pokračovat</Button>
                      </Link>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Sidebar: weak topics + invite */}
        <aside className="col-span-12 lg:col-span-4 space-y-12">
          <div>
            <h3 className="font-serif text-xl text-ink mb-4">Kam investovat čas</h3>
            {data.weak_topics.length === 0 ? (
              <p className="text-sm text-slate">
                Po prvních testech tu uvidíš témata, ve kterých ti utíkají body.
              </p>
            ) : (
              <ul className="space-y-3" data-testid="weak-topics-list">
                {data.weak_topics.map((t) => (
                  <li key={t.topic} className="flex items-center justify-between gap-3">
                    <span className="text-base text-ink capitalize">{t.topic.replaceAll("_", " ")}</span>
                    <span className="font-mono tabular text-sm text-slate">
                      {Math.round(t.accuracy * 100)}%
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {code && (
            <div className="border-l-2 border-navy pl-5 py-2">
              <p className="caption mb-2">Pozvánka pro rodiče</p>
              <p className="font-mono tabular text-2xl text-navy" data-testid="invite-code">{code}</p>
              <p className="text-sm text-slate mt-2">
                Předej tento kód rodiči — uvidí tvůj agregovaný pokrok, ne jednotlivé odpovědi.
              </p>
            </div>
          )}

          <Link to="/testy" data-testid="cta-new-test">
            <Button className="bg-navy hover:bg-navy-dark text-parchment rounded-sm w-full py-6">
              Začít nový test
            </Button>
          </Link>
        </aside>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-bone px-6 py-8">
      <p className="caption mb-3">{label}</p>
      <p className="font-mono tabular text-3xl text-ink">{value}</p>
    </div>
  );
}
